import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import qs from 'qs'
import { getNextPageParam } from 'utils/getNextPageParam'
import { Response } from 'request-dot-js/index'
import { reportError } from 'utils/error'
import { ChatMessage } from 'api/clients/ChatClient'
import { chatKeys } from './chatKeys'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

interface Data {
  messages: ChatMessage[]
}

export interface ThreadMessagesData {
  pages: Array<Response<Data>>
  pageParams: unknown[]
}

interface Params {
  page: number
  items: number
}

interface Props extends Partial<Pick<Params, 'items'>> {
  threadId?: number
}

const fetchMessages = async (workspaceId: number, threadId: number, params: Params) => {
  const stringifiedParams = qs.stringify(params)
  const res = await sendToApi<Data>(`/w/${workspaceId}/threads/${threadId}/messages?${stringifiedParams}`)
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res
}

export const useInfiniteMessages = (
  { threadId, items = 10 }: Props,
  options?: UseInfiniteQueryOptions<Response<Data>>,
) => {
  const workspaceId = useCurrentWorkspaceId()

  return useInfiniteQuery<Response<Data>>(
    chatKeys.messages(workspaceId, threadId || 0, { items }),
    ({ pageParam = 1 }) => {
      if (!threadId) {
        throw new Error('Thread ID is required')
      }
      return fetchMessages(workspaceId, threadId, { page: pageParam, items })
    },
    {
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
      enabled: !!threadId,
      ...options,
    },
  )
}
